import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CloudArrowUpIcon, DocumentTextIcon, PhotoIcon,
  TableCellsIcon, FolderIcon, TrashIcon, ArrowDownTrayIcon,
  XMarkIcon, PaperClipIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { projectDocumentsAPI } from '../../../services/api';

// ── helpers ──────────────────────────────────────────────────────────────────
function getExt(name) { return (name || '').split('.').pop().toLowerCase(); }

function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const EXT_CFG = {
  pdf:  { Icon: DocumentTextIcon, color: 'text-red-500',    bg: 'bg-red-50'    },
  doc:  { Icon: DocumentTextIcon, color: 'text-blue-500',   bg: 'bg-blue-50'   },
  docx: { Icon: DocumentTextIcon, color: 'text-blue-500',   bg: 'bg-blue-50'   },
  xls:  { Icon: TableCellsIcon,   color: 'text-green-500',  bg: 'bg-green-50'  },
  xlsx: { Icon: TableCellsIcon,   color: 'text-green-500',  bg: 'bg-green-50'  },
  jpg:  { Icon: PhotoIcon,        color: 'text-purple-500', bg: 'bg-purple-50' },
  jpeg: { Icon: PhotoIcon,        color: 'text-purple-500', bg: 'bg-purple-50' },
  png:  { Icon: PhotoIcon,        color: 'text-purple-500', bg: 'bg-purple-50' },
  gif:  { Icon: PhotoIcon,        color: 'text-pink-500',   bg: 'bg-pink-50'   },
};
function FileIcon({ name }) {
  const cfg = EXT_CFG[getExt(name)] || { Icon: FolderIcon, color: 'text-gray-500', bg: 'bg-gray-100' };
  return (
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
      <cfg.Icon className={`w-5 h-5 ${cfg.color}`} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
/**
 * StageDocuments
 * Props:
 *   projectId  {string}  MongoDB project _id
 *   stage      {string}  current stage key, e.g. 'planning'
 *   stageColor {string}  tailwind text color class for accents
 *   stageBg    {string}  tailwind bg color class for accents
 */
export function StageDocuments({ projectId, stage, stageColor = 'text-indigo-600', stageBg = 'bg-indigo-50' }) {
  const [docs,      setDocs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging,  setDragging]  = useState(false);
  const [note,      setNote]      = useState('');
  const [showNote,  setShowNote]  = useState(false);
  const [pendingFile, setPendingFile] = useState(null);  // File object awaiting note confirm
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!projectId || !stage) return;
    setLoading(true);
    try {
      const res = await projectDocumentsAPI.getByStage(projectId, stage);
      setDocs(res.data || []);
    } catch { toast.error('Failed to load documents'); }
    finally { setLoading(false); }
  }, [projectId, stage]);

  useEffect(() => { load(); }, [load]);

  // ── upload flow ────────────────────────────────────────────
  const startUpload = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large — max 10 MB'); return; }
    setPendingFile(file);
    setNote('');
    setShowNote(true);
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file',  pendingFile);
      fd.append('stage', stage);
      if (note.trim()) fd.append('note', note.trim());
      await projectDocumentsAPI.upload(projectId, fd);
      toast.success('Document uploaded');
      setShowNote(false);
      setPendingFile(null);
      setNote('');
      await load();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const cancelUpload = () => { setShowNote(false); setPendingFile(null); setNote(''); };

  // ── delete ─────────────────────────────────────────────────
  const handleDelete = async (docId, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await projectDocumentsAPI.remove(projectId, docId);
      toast.success('Deleted');
      setDocs(prev => prev.filter(d => d._id !== docId));
    } catch { toast.error('Delete failed'); }
  };

  // ── download/preview ───────────────────────────────────────
  const handleDownload = (docId, name) => {
    const url = `/api/projects/${projectId}/documents/${docId}/download`;
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ── drag-and-drop ──────────────────────────────────────────
  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) startUpload(file);
  };

  // ── render ─────────────────────────────────────────────────
  return (
    <div className="mt-4 space-y-3">

      {/* ── Upload note/confirm modal ── */}
      {showNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Upload Document</h3>
              <button onClick={cancelUpload} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* File preview row */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <FileIcon name={pendingFile?.name} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-800 truncate">{pendingFile?.name}</p>
                <p className="text-xs text-gray-400">{fmtSize(pendingFile?.size || 0)}</p>
              </div>
            </div>

            {/* Optional note */}
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                Note <span className="normal-case font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={2}
                placeholder="e.g. Approved blueprint v3"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex gap-3">
              <button onClick={cancelUpload}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={confirmUpload} disabled={uploading}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2
                  ${uploading ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                {uploading
                  ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Uploading…</>
                  : <><CheckCircleIcon className="w-4 h-4" /> Upload</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Drop zone ── */}
      <div
        onDragEnter={e => { e.preventDefault(); setDragging(true); }}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-5 flex flex-col items-center gap-2 cursor-pointer transition-all
          ${dragging ? 'border-indigo-400 bg-indigo-50 scale-[1.01]' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'}`}
      >
        <CloudArrowUpIcon className={`w-7 h-7 ${dragging ? 'text-indigo-500' : 'text-gray-400'}`} />
        <p className="text-sm font-medium text-gray-600">
          {dragging ? 'Drop to upload' : 'Click or drag & drop to upload'}
        </p>
        <p className="text-xs text-gray-400">PDF, Word, Excel, images — max 10 MB</p>
        <input ref={fileRef} type="file" className="hidden"
          onChange={e => { if (e.target.files?.[0]) startUpload(e.target.files[0]); e.target.value = ''; }} />
      </div>

      {/* ── Document list ── */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <PaperClipIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No documents uploaded for this stage yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            {docs.length} document{docs.length !== 1 ? 's' : ''}
          </p>
          {docs.map(doc => (
            <div key={doc._id}
              className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl hover:border-gray-200 hover:shadow-sm transition-all group">
              <FileIcon name={doc.name} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-800 truncate">{doc.name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-xs text-gray-400">{fmtSize(doc.size)}</span>
                  <span className="text-gray-200">·</span>
                  <span className="text-xs text-gray-400">{fmtDate(doc.createdAt)}</span>
                  {doc.uploadedBy?.name && <>
                    <span className="text-gray-200">·</span>
                    <span className="text-xs text-gray-400">{doc.uploadedBy.name}</span>
                  </>}
                </div>
                {doc.note && (
                  <p className="text-xs text-gray-500 mt-0.5 italic truncate">"{doc.note}"</p>
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button onClick={() => handleDownload(doc._id, doc.name)}
                  className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors" title="Download / Preview">
                  <ArrowDownTrayIcon className="w-4 h-4 text-indigo-500" />
                </button>
                <button onClick={() => handleDelete(doc._id, doc.name)}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                  <TrashIcon className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}